/**
 * Everything numeric or wordy about sound, in one place (PS5-2/PS5-5, D-094).
 *
 * Scenes and screens hold no audio constants and no audio copy: the same rule the camera,
 * narration and readiness layers already follow. Every value below is **flagged** — derived from
 * something, never measured with a five-year-old in the room, and expected to move at the physical
 * session.
 */

/**
 * What a manifest entry is *for*, and therefore how it behaves when something else must be heard
 * (PS5-2 — ruled necessary before the manifest's first audio entry existed).
 *
 * Three values, not a spectrum: a bed that plays under everything, a short cue that does not, and
 * a spoken line baked to a file (Slice A″ — see `AudioDefinition` in the SDK for why speech is a
 * kind of its own rather than a loud effect).
 */
export type { AudioCategory } from '../../sdk/theme';

/**
 * The one ducking rule, written down once.
 *
 * A sound is attenuated while **something with a strictly higher priority holds the foreground**,
 * and released when the last such claim goes away. That is the whole contract; there is no second
 * rule for a second situation.
 *
 * Narration claims the foreground at {@link AUDIO_TUNABLES.narrationForegroundPriority} for
 * exactly as long as it is speaking. Music is declared below that number and therefore ducks;
 * SFX are declared above it and therefore do not — which is PS5-2's "SFX are short and never duck
 * anything", stated as a property of the *sounds* rather than as an exception in the code. Nothing
 * except narration claims the foreground today, and nothing needs to: a claim is a general
 * mechanism precisely so the next thing that must be heard does not arrive as a special case.
 */
export interface AudioTunables {
  /**
   * Master gain when a family has not chosen one (`ParentSettings.soundVolume` default).
   *
   * **Derivation:** narration's default is 1.0 and speech is the load-bearing sound in this
   * product — a child who mishears an instruction has lost the round. Sound effects and music are
   * atmosphere. 0.7 sits a little under speech at the same system volume, so a family that has
   * set their laptop for comfortable narration does not then have to turn the whole thing down.
   * Flagged: the only honest way to set this is to listen to it on the target machine.
   */
  readonly defaultSoundVolume: number;
  /**
   * Per-track gain for the music bed, before the master volume.
   *
   * **Derivation:** a bed is meant to be noticed only when it stops. 0.35 is roughly a third of
   * the chime's level, which is the ratio a bed and a cue want; it is set here rather than in the
   * manifest's `volume` field so the *theme* can still say "this particular loop is a quiet one"
   * relative to it. Flagged.
   */
  readonly musicGain: number;
  /**
   * Per-track gain for a cue.
   *
   * **Derivation:** a celebration cue has to be audible over the bed while narration is *not*
   * speaking, which means comfortably above {@link musicGain} but well short of clipping when it
   * lands on top of the bed (0.6 + 0.35 = 0.95 of one voice's headroom, and the two are never at
   * peak in the same millisecond). Flagged.
   */
  readonly sfxGain: number;
  /**
   * Per-track gain for a baked spoken line (Slice A″).
   *
   * **Derivation:** 1.0, and deliberately not less. Speech is the load-bearing sound in this
   * product — a child who mishears the question has lost the round — and the browser voice it
   * replaces has always been played at full narration volume. The family's own narration volume is
   * applied on top of this by the caller, and the master `soundVolume` scales everything after
   * that, so there are already two places a grown-up can turn speech down; a third, invisible one
   * would only make the two visible ones lie. Flagged, like everything here.
   */
  readonly narrationGain: number;
  /**
   * How far a ducked sound drops, as a fraction of its own gain.
   *
   * **Derivation:** the requirement is that a five-year-old hears every word over the bed, not
   * that the bed disappears — a bed that cuts to silence draws attention to itself twice, once
   * going and once coming back. 0.3 is a ~10 dB drop, the usual broadcast voice-over duck, which
   * puts the bed a clear order of magnitude below speech while leaving it present. Flagged: the
   * number that matters is intelligibility on a laptop speaker, and that is a listening test.
   */
  readonly duckedGain: number;
  /**
   * How long the drop takes.
   *
   * **Derivation:** must be short enough that the duck is complete before the first syllable —
   * narration starts speaking within a few tens of milliseconds of the claim — and long enough
   * not to click. 120 ms is under a syllable and well over the ~10 ms where a gain change becomes
   * audible as a transient. Flagged.
   */
  readonly duckAttackMs: number;
  /**
   * How long the recovery takes.
   *
   * **Derivation:** deliberately several times the attack. A bed that snaps back the instant a
   * sentence ends punctuates the silence and reads as an event; a slow return reads as nothing at
   * all, which is what a bed is for. 400 ms is also longer than the gap between two queued
   * narration lines would be, so back-to-back sentences do not pump. Flagged.
   */
  readonly duckReleaseMs: number;
  /**
   * The priority narration holds while it is speaking.
   *
   * **Derivation:** a round number in the middle of the range, so theme entries can be declared
   * on either side of it without renumbering. It is meaningful only relative to the
   * `duckPriority` a manifest entry declares.
   */
  readonly narrationForegroundPriority: number;
}

export const AUDIO_TUNABLES: AudioTunables = {
  defaultSoundVolume: 0.7,
  musicGain: 0.35,
  sfxGain: 0.6,
  narrationGain: 1,
  duckedGain: 0.3,
  duckAttackMs: 120,
  duckReleaseMs: 400,
  narrationForegroundPriority: 50,
};

/**
 * Grown-up-facing wording for the sound controls (FLAGGED for Jon's copy table).
 *
 * Invented here, in the same register as the narration and motion controls beside it: names what
 * it does, promises nothing about a child, and never uses the word "off" as a judgement.
 */
export const INVENTED_AUDIO_COPY = {
  /** The mute control, on both grown-up surfaces. Must not contain the word "narration". */
  soundLabel: 'Sound (gentle music and chimes)',
  soundVolumeLabel: 'Sound volume',
  /** Sits under the control on the setup screen. */
  soundNote: 'Music plays quietly under the mission and dips whenever Bip-Bop speaks.',
} as const;

/**
 * The sounds the **platform** plays, named by convention (PS5-6's ID scheme).
 *
 * The platform has moments a game does not own — a mission beginning, a team celebration
 * completing — and something has to say which sound belongs to each. Two shapes were available:
 * add a role field to every manifest entry, or reserve well-known IDs the way
 * `categoryPlaceholders` reserves well-known placeholder slots. The second is chosen because it
 * costs the module contract nothing and keeps the direction of the dependency right: the platform
 * asks for an ID, the theme decides whether it has one, and **a theme that declares neither is
 * simply a quiet theme**. Nothing here is a filename, and nothing here reaches into a game.
 *
 * A game that wants a different celebration cue changes the file behind its own ID. A game that
 * wants no music deletes the entry.
 */
export const PLATFORM_AUDIO_ROLES = {
  /**
   * The team's celebration cue.
   *
   * **Team-level and nothing else** (AUD-05, and the locked non-competitive invariant). It plays
   * for the moment, not for a child: the same sound whether two children joined in, one did, or
   * neither, and there is deliberately no per-player variant for anybody to add later. A sound
   * that told a room which child moved would be a ranking made of noise.
   */
  teamSuccess: 'sfx.team-success.v1',
  /** The mission's music bed. Gentle, unhurried, and ducked whenever anything is spoken. */
  missionLoop: 'music.mission-loop.v1',
} as const;
